"use client";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import clsx, { ClassValue } from "clsx";

import Link from "next/link";

const TEXT_ALIGN_CLASS = {
  left: "text-left",
  right: "text-right",
  center: "text-center",
} as const;

const TYPE_STYLES = {
  primary: {
    enabled: "bg-black hover:bg-gail-gray active:bg-gail-gray text-white",
    disabled: "bg-black/50 text-white/70",
  },
  secondary: {
    enabled:
      "bg-gail-primary hover:bg-gail-primary-hover active:bg-gail-primary-hover text-white",
    disabled: "bg-gail-primary/50 text-white",
  },
  tertiary: {
    enabled: "bg-white hover:bg-gail-silver active:bg-gail-silver text-black",
    disabled: "bg-white/70 text-black/50",
  },
  none: {
    enabled: "m-[2px]",
    disabled: "m-[2px]",
  },
} as const;

export type ButtonProps = {
  children?: React.ReactNode;
  label?: React.ReactNode;
  type?: "primary" | "secondary" | "tertiary" | "none";
  htmlType?: "button" | "submit" | "reset";
  compact?: boolean;
  tiny?: boolean;
  wide?: boolean;
  disabled?: boolean;
  restrictedText?: string;
  id?: string;
  href?: string;
  target?: "_blank" | "_self" | "_parent" | "_top";
  rel?: string;
  role?: React.AriaRole;
  ariaCurrent?: React.AriaAttributes["aria-current"];
  ariaSelected?: boolean;
  onClick?: React.MouseEventHandler<HTMLElement>;
  secondaryAction?: () => void;
  iconBefore?: React.ReactNode;
  iconAfter?: React.ReactNode;
  testId?: string;
  className?: ClassValue;
  prefetch?: boolean;
  textAlign?: "left" | "center" | "right";
  textClassName?: ClassValue;
};

const Button: React.FC<ButtonProps> = ({
  href,
  onClick,
  type = "primary",
  htmlType,
  className,
  id,
  testId: dataTestId,
  role,
  ariaCurrent,
  ariaSelected,
  wide,
  compact,
  disabled,
  tiny,
  restrictedText,
  children,
  iconBefore,
  iconAfter,
  prefetch = true,
  target = "_self",
  rel,
  secondaryAction,
  textAlign = "center",
  textClassName,
}) => {
  if (
    !href &&
    !onClick &&
    !htmlType &&
    process.env.NEXT_PUBLIC_ENVIRONMENT === "development"
  ) {
    throw new Error("You must have an href, onClick, or htmlType prop");
  }

  if (restrictedText) {
    disabled = true;
  }

  const commonProps = {
    id,
    role,
    disabled,
    "aria-current": ariaCurrent,
    "aria-selected": ariaSelected,
    "data-testid": dataTestId,
    className: clsx(
      "rounded-full",
      {
        "py-2 px-4": !compact,
        "py-1 px-2": compact,
        "cursor-not-allowed": disabled,
        "cursor-pointer": !disabled,
        "w-full": wide,
        flex: !!href,
        "inline-block": !href && !!onClick,
      },
      TYPE_STYLES[type][disabled ? "disabled" : "enabled"],
      className,
    ),
  };

  const textAlignClass = TEXT_ALIGN_CLASS[textAlign];

  const buttonChildren = (
    <span
      className={clsx(
        textAlignClass,
        "m-auto",
        {
          ["text-sm"]: compact && !textClassName,
          ["text-[10.5px]"]: tiny && !textClassName,
        },
        textClassName,
      )}
    >
      {iconBefore && (
        <span className={clsx("mr-2", "text-current")}>{iconBefore}</span>
      )}
      {children}
      {iconAfter && <span className="ml-2">{iconAfter}</span>}
    </span>
  );

  if (href) {
    const linkComponent = (
      <Link
        href={href}
        target={target}
        rel={rel}
        {...commonProps}
        prefetch={prefetch}
        onClick={(event) => {
          onClick?.(event);
          if (secondaryAction) {
            secondaryAction();
          }
        }}
      >
        {buttonChildren}
      </Link>
    );

    if (restrictedText) {
      return (
        <div className={clsx({ "[&>div:first-of-type]:w-full": wide })}>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>{linkComponent}</TooltipTrigger>
              <TooltipContent>{restrictedText}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      );
    }

    return linkComponent;
  }

  if (onClick || htmlType) {
    const buttonComponent = (
      <button onClick={onClick} type={htmlType ?? "button"} {...commonProps}>
        {buttonChildren}
      </button>
    );

    if (restrictedText) {
      return (
        <div className={clsx({ "[&>div:first-of-type]:w-full": wide })}>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>{buttonComponent}</TooltipTrigger>
              <TooltipContent>{restrictedText}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      );
    }

    return buttonComponent;
  }
};

export default Button;
